import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarComponent } from '../ui/calendar';
import {
  FileText, Receipt, Loader2, AlertCircle, DollarSign,
  ChevronDown, ChevronUp, RefreshCw, Calendar, FilePlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parse } from 'date-fns';

function formatCurrency(val) {
  if (val == null) return '$0.00';
  return `$${Number(val).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try { return format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy'); } catch { return dateStr; }
}

export function TowerCraneDocketsInvoicing({ jobId }) {
  const { api } = useAuth();
  const [dockets, setDockets] = useState([]);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [loadingDockets, setLoadingDockets] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(true);
  const [previewError, setPreviewError] = useState(null);

  // Date range for invoice preview
  const [previewEndDate, setPreviewEndDate] = useState('');
  const [endDateOpen, setEndDateOpen] = useState(false);

  // Generate invoice
  const [generating, setGenerating] = useState(false);

  // Existing invoices for this job
  const [existingInvoices, setExistingInvoices] = useState([]);

  const fetchDockets = useCallback(async () => {
    try {
      const res = await api.get(`/dockets/job/${jobId}`);
      setDockets(res.data || []);
    } catch { setDockets([]); }
    finally { setLoadingDockets(false); }
  }, [api, jobId]);

  const fetchInvoicePreview = useCallback(async (endDate) => {
    setLoadingPreview(true);
    setPreviewError(null);
    try {
      const params = endDate ? `?up_to_date=${endDate}` : '';
      const res = await api.get(`/tower-cranes/${jobId}/invoice-preview${params}`);
      setInvoicePreview(res.data);
    } catch (err) {
      setPreviewError(err.response?.data?.detail || 'Could not load invoice preview');
      setInvoicePreview(null);
    } finally {
      setLoadingPreview(false);
    }
  }, [api, jobId]);

  const fetchExistingInvoices = useCallback(async () => {
    try {
      const res = await api.get(`/dry-hires/${jobId}/invoices`);
      setExistingInvoices(res.data?.items || []);
    } catch { setExistingInvoices([]); }
  }, [api, jobId]);

  useEffect(() => {
    fetchDockets();
    fetchInvoicePreview(previewEndDate);
    fetchExistingInvoices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEndDateChange = (d) => {
    if (!d) return;
    const dateStr = format(d, 'yyyy-MM-dd');
    setPreviewEndDate(dateStr);
    setEndDateOpen(false);
    fetchInvoicePreview(dateStr);
  };

  const handleClearEndDate = () => {
    setPreviewEndDate('');
    setEndDateOpen(false);
    fetchInvoicePreview('');
  };

  const handleRefresh = () => {
    fetchInvoicePreview(previewEndDate);
  };

  const handleGenerateInvoice = async () => {
    if (!invoicePreview) return;
    const hasLineItems = (invoicePreview.equipment_line_items?.length > 0 || invoicePreview.labour_line_items?.length > 0);
    if (!hasLineItems) {
      toast.error('No chargeable items to invoice');
      return;
    }
    setGenerating(true);
    try {
      const upToDate = previewEndDate || invoicePreview.window_end;
      await api.post(`/dry-hires/${jobId}/partial-invoice`, { up_to_date: upToDate });
      toast.success('Invoice generated successfully');
      // Refresh preview (window will shift) and invoices list
      await Promise.all([fetchInvoicePreview(''), fetchExistingInvoices()]);
      setPreviewEndDate('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      submitted: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
      rejected: 'bg-red-500/10 text-red-400 border-red-500/30',
      draft: 'bg-muted text-muted-foreground',
      sent: 'bg-violet-500/10 text-violet-400 border-violet-500/30',
      finalised: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    };
    return map[status] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="space-y-6" data-testid="tc-dockets-invoicing-tab">
      {/* ── Dockets Table ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" /> Submitted Dockets
            </CardTitle>
            <Badge variant="outline" className="text-[10px]">
              {dockets.length} docket{dockets.length !== 1 ? 's' : ''}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loadingDockets ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : dockets.length === 0 ? (
            <div className="text-center py-6 border border-dashed rounded-md">
              <FileText className="h-6 w-6 mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">No dockets submitted for this job yet</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Dockets are created by operators from the mobile app</p>
            </div>
          ) : (
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm" data-testid="tc-dockets-table">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Docket #</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Date</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Operator</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Items</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Status</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {dockets.map(d => (
                    <tr key={d.id} className="border-b last:border-0 hover:bg-muted/10 transition-colors">
                      <td className="px-3 py-2 font-mono text-xs">{d.docket_number || '-'}</td>
                      <td className="px-3 py-2 text-xs">{formatDate(d.job_date)}</td>
                      <td className="px-3 py-2 text-xs truncate max-w-[140px]">{d.operator_name || d.worker_name || '-'}</td>
                      <td className="px-3 py-2 text-xs">{d.items?.length || 0}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`text-[10px] capitalize ${statusBadge(d.status)}`}>
                          {d.status || 'draft'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-xs text-right font-mono">
                        {formatCurrency(d.items?.reduce((s, i) => s + ((i.qty || 0) * (i.unit_price || 0)), 0))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Invoice Preview ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Receipt className="h-4 w-4 text-muted-foreground" /> Invoice Preview
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost" size="sm" className="h-7 text-xs"
                onClick={handleRefresh} disabled={loadingPreview}
                data-testid="tc-refresh-invoice-preview"
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${loadingPreview ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              <Button
                variant="ghost" size="icon" className="h-7 w-7"
                onClick={() => setPreviewExpanded(!previewExpanded)}
              >
                {previewExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>
        {previewExpanded && (
          <CardContent className="space-y-4">
            {/* Date range selector */}
            <div className="flex flex-wrap items-end gap-3 p-3 bg-muted/20 border border-border rounded-md" data-testid="tc-invoice-date-range">
              <div>
                <Label className="text-[10px] text-muted-foreground">From</Label>
                <div className="text-sm font-medium mt-0.5">
                  {invoicePreview?.window_start ? formatDate(invoicePreview.window_start) : 'Job start'}
                </div>
                <p className="text-[10px] text-muted-foreground">Auto-calculated</p>
              </div>
              <div className="text-muted-foreground text-sm px-1">—</div>
              <div>
                <Label className="text-[10px] text-muted-foreground">To</Label>
                <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="h-8 text-xs font-normal min-w-[140px] justify-start" data-testid="tc-invoice-end-date">
                      <Calendar className="mr-1.5 h-3 w-3" />
                      {previewEndDate ? formatDate(previewEndDate) : 'Today (default)'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={previewEndDate ? parse(previewEndDate, 'yyyy-MM-dd', new Date()) : undefined}
                      onSelect={handleEndDateChange}
                    />
                    {previewEndDate && (
                      <div className="p-2 border-t">
                        <Button variant="ghost" size="sm" className="w-full text-xs" onClick={handleClearEndDate}>
                          Reset to today
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
              {invoicePreview?.billing_period && (
                <div className="ml-auto">
                  <Badge variant="outline" className="text-[10px] capitalize">{invoicePreview.billing_period}</Badge>
                </div>
              )}
            </div>

            {loadingPreview ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : previewError ? (
              <div className="flex items-start gap-2 text-sm text-amber-400 bg-amber-500/5 border border-amber-500/20 rounded-md p-3">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{previewError}</span>
              </div>
            ) : invoicePreview ? (
              <div className="space-y-4">
                {/* Summary stats */}
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                  {invoicePreview.chargeable_days != null && <span>Chargeable days: <strong className="text-foreground">{invoicePreview.chargeable_days}</strong></span>}
                  {invoicePreview.leftover_days > 0 && (
                    <span>Leftover days: <strong className="text-amber-400">{invoicePreview.leftover_days}</strong>
                      <span className="text-muted-foreground/60 ml-0.5">(rolls to next invoice)</span>
                    </span>
                  )}
                  {invoicePreview.weekend_days_excluded > 0 && <span>Weekends excluded: <strong className="text-foreground">{invoicePreview.weekend_days_excluded}</strong></span>}
                  {invoicePreview.docket_count != null && <span>Dockets: <strong className="text-foreground">{invoicePreview.docket_count}</strong></span>}
                  {invoicePreview.price_list_name && <span>Price list: <strong className="text-foreground">{invoicePreview.price_list_name}</strong></span>}
                </div>

                {/* Equipment line items */}
                {invoicePreview.equipment_line_items?.length > 0 && (
                  <div>
                    <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <DollarSign className="h-3 w-3" /> Equipment Charges
                    </h5>
                    <div className="border rounded-md overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/30">
                            <th className="text-left px-3 py-1.5 font-medium text-muted-foreground">Description</th>
                            <th className="text-right px-3 py-1.5 font-medium text-muted-foreground">Qty</th>
                            <th className="text-right px-3 py-1.5 font-medium text-muted-foreground">Rate</th>
                            <th className="text-right px-3 py-1.5 font-medium text-muted-foreground">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoicePreview.equipment_line_items.map((item, idx) => (
                            <tr key={`eq-${idx}`} className="border-b last:border-0">
                              <td className="px-3 py-1.5">{item.description || item.item_name || '-'}</td>
                              <td className="px-3 py-1.5 text-right font-mono">{item.qty ?? item.quantity ?? '-'}</td>
                              <td className="px-3 py-1.5 text-right font-mono">{formatCurrency(item.unit_price ?? item.rate)}</td>
                              <td className="px-3 py-1.5 text-right font-mono">{formatCurrency(item.amount ?? item.line_total)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-muted/20">
                            <td colSpan={3} className="px-3 py-1.5 text-right font-medium">Equipment Subtotal</td>
                            <td className="px-3 py-1.5 text-right font-mono font-medium">{formatCurrency(invoicePreview.equipment_subtotal)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {/* Labour line items */}
                {invoicePreview.labour_line_items?.length > 0 && (
                  <div>
                    <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <DollarSign className="h-3 w-3" /> Labour Charges
                    </h5>
                    <div className="border rounded-md overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/30">
                            <th className="text-left px-3 py-1.5 font-medium text-muted-foreground">Description</th>
                            <th className="text-right px-3 py-1.5 font-medium text-muted-foreground">Hours</th>
                            <th className="text-right px-3 py-1.5 font-medium text-muted-foreground">Rate</th>
                            <th className="text-right px-3 py-1.5 font-medium text-muted-foreground">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoicePreview.labour_line_items.map((item, idx) => (
                            <tr key={`lb-${idx}`} className="border-b last:border-0">
                              <td className="px-3 py-1.5">{item.description || item.type_name || '-'}</td>
                              <td className="px-3 py-1.5 text-right font-mono">{item.total_hours ?? item.qty ?? '-'}</td>
                              <td className="px-3 py-1.5 text-right font-mono">{formatCurrency(item.unit_price ?? item.rate)}</td>
                              <td className="px-3 py-1.5 text-right font-mono">{formatCurrency(item.amount ?? item.line_total)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-muted/20">
                            <td colSpan={3} className="px-3 py-1.5 text-right font-medium">Labour Subtotal</td>
                            <td className="px-3 py-1.5 text-right font-mono font-medium">{formatCurrency(invoicePreview.labour_subtotal)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {/* No line items at all */}
                {(!invoicePreview.equipment_line_items?.length && !invoicePreview.labour_line_items?.length) && (
                  <div className="text-center py-4 border border-dashed rounded-md">
                    <Receipt className="h-6 w-6 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-sm text-muted-foreground">No chargeable items in this billing window</p>
                  </div>
                )}

                {/* Warnings */}
                {invoicePreview.warnings?.length > 0 && (
                  <div className="space-y-1">
                    {invoicePreview.warnings.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-amber-400 bg-amber-500/5 border border-amber-500/20 rounded-md p-2">
                        <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
                        <span>{w}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Missing rates */}
                {invoicePreview.missing_rates?.length > 0 && (
                  <div className="flex items-start gap-2 text-xs text-red-400 bg-red-500/5 border border-red-500/20 rounded-md p-2">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>Missing rates for: {invoicePreview.missing_rates.join(', ')}</span>
                  </div>
                )}

                {/* Combined total + Generate button */}
                <div className="flex items-center justify-between border-t pt-3">
                  <div>
                    <span className="text-sm font-medium">Combined Total</span>
                    <span className="text-lg font-bold font-mono ml-3" data-testid="tc-invoice-combined-total">
                      {formatCurrency(invoicePreview.combined_subtotal)}
                    </span>
                  </div>
                  <Button
                    onClick={handleGenerateInvoice}
                    disabled={generating || (!invoicePreview.equipment_line_items?.length && !invoicePreview.labour_line_items?.length)}
                    className="bg-[#ffd108] text-black hover:bg-[#e5bc07] min-w-[160px]"
                    data-testid="tc-generate-invoice-btn"
                  >
                    {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FilePlus className="h-4 w-4 mr-2" />}
                    Generate Invoice
                  </Button>
                </div>
              </div>
            ) : null}
          </CardContent>
        )}
      </Card>

      {/* ── Previously Generated Invoices ── */}
      {existingInvoices.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FilePlus className="h-4 w-4 text-muted-foreground" /> Generated Invoices
              </CardTitle>
              <Badge variant="outline" className="text-[10px]">
                {existingInvoices.length} invoice{existingInvoices.length !== 1 ? 's' : ''}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm" data-testid="tc-invoices-table">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Type</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Window</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Items</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Status</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Total</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {existingInvoices.map(inv => (
                    <tr key={inv.id} className="border-b last:border-0 hover:bg-muted/10 transition-colors">
                      <td className="px-3 py-2 text-xs capitalize">{inv.invoice_type || 'partial'}</td>
                      <td className="px-3 py-2 text-xs">
                        {formatDate(inv.window_start)} — {formatDate(inv.window_end)}
                      </td>
                      <td className="px-3 py-2 text-xs">{inv.line_items?.length || 0}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`text-[10px] capitalize ${statusBadge(inv.status)}`}>
                          {inv.status || 'draft'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-xs text-right font-mono">{formatCurrency(inv.subtotal)}</td>
                      <td className="px-3 py-2 text-xs text-right text-muted-foreground">
                        {inv.created_at ? formatDate(inv.created_at.split('T')[0]) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
